/**
 * Narrowed-export controls (legal-review follow-up 2026-10-25).
 *
 * Collapsible panel below the usual Export buttons. Lets the merchant
 * download a filtered subset instead of the whole day: attack-match
 * records across a date range, or every record from one caller (the
 * caller of the record this panel is attached to).
 *
 * The server-side filter is applied under the authenticated principal;
 * the UI simply chooses the kind and the UTC range.
 */

import { useMemo, useState } from 'react';

import type { AuditRecord } from '@sill/types';
import type { AuditBundleScope } from '@sill/types/api';
import type { SillApi } from '../../api/contract.js';
import { useC } from '../../lib/theme.js';
import {
  exportAuditNarrowedBundle,
  exportErrorMessage,
  narrowedExportErrorMessage,
} from './export.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const MAX_SPAN_DAYS = 31;

/** Format a Date as `YYYY-MM-DD` in UTC. */
function utcDate(d: Date): string {
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysBetween(from: string, to: string): number {
  const f = Date.parse(`${from}T00:00:00Z`);
  const t = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(f) || Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return Math.round((t - f) / (24 * 60 * 60 * 1000)) + 1;
}

export interface NarrowedExportPanelProps {
  readonly api: SillApi;
  readonly record: AuditRecord;
  readonly siteDomain?: string | undefined;
}

export function NarrowedExportPanel({
  api,
  record,
  siteDomain,
}: NarrowedExportPanelProps): JSX.Element {
  const C = useC();
  const today = useMemo(() => utcDate(new Date()), []);
  const seedDay = useMemo(() => (record.evaluated_at ?? today).slice(0, 10), [record.evaluated_at, today]);

  const [scope, setScope] = useState<AuditBundleScope>('attack');
  const [from, setFrom] = useState<string>(seedDay);
  const [to, setTo] = useState<string>(seedDay);
  const [ext, setExt] = useState<'json' | 'html'>('html');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const span = daysBetween(from, to);
  const invalidRange = !/^\d{4}-\d{2}-\d{2}$/.test(from)
    || !/^\d{4}-\d{2}-\d{2}$/.test(to)
    || from > to
    || span > MAX_SPAN_DAYS;

  const onDownload = async (): Promise<void> => {
    setErr(null);
    setBusy(true);
    try {
      await exportAuditNarrowedBundle(
        api,
        record,
        { scope, from, to, ext },
        siteDomain,
      );
    } catch (e) {
      // Dedicated 422/429 message when the range covers too many records
      // or the per-account burst limit has been hit; falls back to the
      // generic export error for everything else.
      const specific = narrowedExportErrorMessage(e);
      setErr(specific !== null ? specific : exportErrorMessage('Export', e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <details
      className="mt-2"
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        padding: '8px 12px',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: C.muted,
        }}
      >
        Narrowed export (filter)
      </summary>
      <p
        className="mt-2"
        style={{ fontFamily: sansFont, fontSize: 12, color: C.body, lineHeight: 1.5 }}
      >
        A filtered subset over a UTC range of up to {MAX_SPAN_DAYS} days. Each record keeps its own signature and the file is signed as a unit. A narrowed file cannot show that the day is complete; the full-day files and their sealed day roots are the way to check completeness.
      </p>
      <div className="mt-2 flex flex-wrap gap-2 items-center">
        <label style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
          Scope
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as AuditBundleScope)}
            style={{
              display: 'block',
              marginTop: 2,
              background: C.surface2 ?? 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 8px',
              fontFamily: sansFont,
              fontSize: 12,
            }}
          >
            <option value="attack">Attack records only</option>
            <option value="caller">One caller</option>
          </select>
        </label>
        <label style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
          From (UTC)
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{
              display: 'block',
              marginTop: 2,
              background: C.surface2 ?? 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 8px',
              fontFamily: sansFont,
              fontSize: 12,
            }}
          />
        </label>
        <label style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
          To (UTC)
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={{
              display: 'block',
              marginTop: 2,
              background: C.surface2 ?? 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 8px',
              fontFamily: sansFont,
              fontSize: 12,
            }}
          />
        </label>
        <label style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1 }}>
          Format
          <select
            value={ext}
            onChange={(e) => setExt(e.target.value === 'json' ? 'json' : 'html')}
            style={{
              display: 'block',
              marginTop: 2,
              background: C.surface2 ?? 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 8px',
              fontFamily: sansFont,
              fontSize: 12,
            }}
          >
            <option value="html">HTML</option>
            <option value="json">JSON</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => void onDownload()}
          disabled={busy || invalidRange}
          data-testid="narrowed-export-download"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            fontWeight: 600,
            padding: '7px 14px',
            background: C.lime,
            color: C.ink,
            border: 'none',
            borderRadius: 2,
            cursor: busy || invalidRange ? 'not-allowed' : 'pointer',
            opacity: busy || invalidRange ? 0.5 : 1,
            alignSelf: 'flex-end',
            marginTop: 14,
          }}
        >
          {busy ? 'Downloading...' : 'Download narrowed file'}
        </button>
      </div>
      {invalidRange ? (
        <div
          className="mt-2"
          style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}
        >
          Pick a UTC range of up to {MAX_SPAN_DAYS} days, with the From date on or before the To date.
        </div>
      ) : null}
      {err !== null ? (
        <div
          className="mt-2 p-2"
          style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2, fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.4 }}
        >
          {err}
        </div>
      ) : null}
    </details>
  );
}
